/**
 * projectionSource.js — THE one place a player's projection comes from.
 *
 * Before this file there were three answers to "what does this player project
 * for?" and they disagreed: `engine/projections.js` (rank decay off
 * STAT_BASELINES) fed the price board and the results page, `projlabProjection`
 * fed the Lab and My Value, and neither knew about the other. This resolves it
 * once, in tiers, and every consumer reads the same number.
 *
 * RESOLUTION ORDER — first hit wins:
 *
 *   (a) THE USER'S OWN SAVED PROJECTION.  Any `projlab-<TEAM>` save, read through
 *       the existing `safeReadProjlab` path. EDITS WIN, unconditionally. This is
 *       deliberately NOT gated on `progress.state === "done"` the way
 *       `loadAllProjectedPlayers` is: a save IS an edit, and a user who touched a
 *       team should see their number on the board even mid-wizard.
 *
 *   (b) DEFAULT_PROJECTIONS (`data/defaultProjections2026.js`) — the house
 *       projections, 257 players / 32 teams, generated from Ben's calibrated
 *       backup by `npm run gen-projections`.
 *
 *   (c) THE MARKET CURVE, for anyone in neither: preseason ADP posRank ->
 *       expected points, with a log(pick) prior for players carrying 2026 draft
 *       capital. This REPLACES the STAT_BASELINES decay curve for QB/RB/WR/TE.
 *
 * `projectAllDefaults` is deliberately NOT a tier. Per the chunk-1 union
 * measurement (docs/reference/projections-chunk1-report-aug18.md §3) it adds
 * exactly ONE player of coverage on top of (b) across 10/12/14 teams, and buys a
 * permanent dependency on a staler depth chart to do it. It stays untouched for
 * ProjLab's own wizard seeding.
 *
 * STATS ARE TRUTH, POINTS ARE A VIEW. Every tier returns a stat line plus
 * `games`; points come from `scoreFromStats(stats, pos, scoring, games)` so the
 * league lens (PPR weight, TE premium, yardage bonuses via expectedBonusGames)
 * applies at read time and switching leagues never touches a projection.
 *
 * 🔴 DEF AND K ARE CARVED OUT, NOT FORGOTTEN. `scoreFromStats` has three
 * branches — QB, RB, and everything-else-as-receiver — so a DEF fed through it
 * scores ~0. Neither (b) nor (c) covers those positions either (the backup has
 * no DEF/K rows and the market curve is fit on skill positions). So DEF/K keep
 * the legacy `generatePlayerStats`/`calculateProjection` path and are tagged
 * `SOURCE_LEGACY`. Do not "simplify" that away.
 *
 * 🔴 THIS FILE MUST NEVER REACH THE AI. `calcAiMaxBidV2` anchors to TIER_MAPS and
 * `dynamicPrices`, and `dynamicPrices` is built from posRank -> tier ladder, not
 * from projections (usePricing.js:218-375). The seeded digests are the proof:
 * gate 285538690 / smithtown 3185394932 / att 836282492 / sc 4073893077 must not
 * move when this file changes. If one does, a projection has leaked into the
 * bidding path — stop the line.
 */

import { DEFAULT_PROJECTIONS } from "../data/defaultProjections2026";
import { DRAFT_CAPITAL_2026 } from "../data/draftCapital2026";
import { UNIQUE_PLAYERS } from "../data/players";
import { getEffectivePosRank, parseRankNum } from "../data/adpData";
import { scoreFromStats, FULL_PPR_LEGACY } from "./projlabProjection";
import { listProjlabTeamKeys, safeReadProjlab, removeProjlabTeam, PROJLAB_PREFIX } from "./projlabStorage";
import { clearAllPlayerEdits, clearAllDepthOverrides } from "./depthOverrides";
import { generatePlayerStats, calculateProjection } from "../engine/projections";

export const SOURCE_USER = "user";
export const SOURCE_DEFAULT = "default";
export const SOURCE_FALLBACK = "fallback";
export const SOURCE_LEGACY = "legacy";

export const CURVE_POSITIONS = ["QB", "RB", "WR", "TE"];

// Same normalisation as draftValuesBridge.draftValueKey / projlab's findPlayerHist.
// Keep this regex identical to those — a key built differently silently misses.
const SUFFIX_RE = /\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i;
export const stripSuffix = (name) => (name || "").replace(SUFFIX_RE, "").trim();
export const projKey = (name, pos) => `${stripSuffix(name)}-${pos}`;

// ── Stat-vocabulary bridge ─────────────────────────────────────────────────
// The two engines never agreed on names. projlab (and therefore every tier here)
// writes `rec`/`int`/`comp`/`targets`/`rush`; engine/projections.js and the
// STAT_LABELS display map want `receptions`/`passInt`/`passComp`/`tgt`/`rushAtt`.
// Points are always computed on the projlab side; this map exists ONLY so the
// PlayerList expanded stat rows can render the same numbers they are scored from.
const TO_ENGINE_VOCAB = {
  rec: "receptions", int: "passInt", comp: "passComp", targets: "tgt", rush: "rushAtt",
};

export function toEngineVocab(stats) {
  const out = {};
  for (const [k, v] of Object.entries(stats || {})) out[TO_ENGINE_VOCAB[k] || k] = v;
  return out;
}

// ── Tier (b): the house projections, indexed once ──────────────────────────
let _defaultIndex = null;
function defaultIndex() {
  if (_defaultIndex) return _defaultIndex;
  const idx = new Map();
  for (const [team, rows] of Object.entries(DEFAULT_PROJECTIONS)) {
    for (const r of rows) {
      idx.set(projKey(r.player, r.pos), { stats: r.stats, games: r.games, team, slot: r.slot });
    }
  }
  _defaultIndex = idx;
  return idx;
}

// ── Tier (a): the user's saved projections ─────────────────────────────────
// Built lazily and memoised: `replacementLevels` in usePricing sorts the whole
// player pool through this, so an un-memoised localStorage read per player would
// be ~500 JSON.parse calls per render. Call invalidateProjectionCache() after any
// projlab save.
/** Every team abbrev that has a projlab save key, saved or not. */
export function listUserTeams() {
  try { return listProjlabTeamKeys().map((k) => k.slice(PROJLAB_PREFIX.length)); }
  catch { return []; }
}

/**
 * One team's rows from the user's save. UNCACHED and uncached on purpose: the
 * Rankings tab can be opened from inside the editor, after an autosave and
 * without passing through any of the invalidation sites, so its read has to be
 * live. Returns [] when there is no usable save — which is also how callers tell
 * "this team is the user's" from "this team is the house's".
 */
export function readUserTeamRows(abbrev) {
  let saved = null;
  try { saved = safeReadProjlab(abbrev); } catch { return []; }
  const rows = saved?.effSummary?.players;
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const pl of rows) {
    if (!pl?.player || !pl.stats) continue;
    // Games backfill for saves predating buildPlayerResult carrying `games`:
    // it lives on the matching stint. Preserve-only; never migrate a save.
    let games = pl.games;
    if (games == null) {
      const baseSlot = String(pl.slot || "").split("#")[0];
      const stintId = String(pl.slot || "").includes("#") ? pl.slot : `${pl.slot}#0`;
      const stints = saved.allocSummary?.allocs?.[baseSlot]?.stints || [];
      games = stints.find((s) => s.id === stintId)?.games ?? 17;
    }
    out.push({ slot: pl.slot, player: pl.player, pos: pl.pos, stats: pl.stats, games, team: abbrev });
  }
  return out;
}

/** House rows for one team. */
export function readHouseTeamRows(abbrev) {
  return (DEFAULT_PROJECTIONS[abbrev] || []).map((r) => ({ ...r, team: abbrev }));
}

/**
 * 🔴 THE PER-TEAM RESOLUTION RULE, in one place. A team is EITHER the user's or
 * the house's — never a merge of the two. That matters: if a user charts ATL and
 * drops a receiver the house set lists, merging by player would silently hand
 * that receiver back. Resolution is per TEAM precisely so an edit can REMOVE a
 * player, not just change one.
 */
export function resolveTeamRows(abbrev) {
  const user = readUserTeamRows(abbrev);
  if (user.length) return { rows: user, source: SOURCE_USER };
  return { rows: readHouseTeamRows(abbrev), source: SOURCE_DEFAULT };
}

/** Every team the app can project: the 32 house teams plus any saved oddity. */
export function allTeamAbbrevs() {
  return [...new Set([...Object.keys(DEFAULT_PROJECTIONS), ...listUserTeams()])].sort();
}

// Memoised name-pos index of the user's rows, for the per-PLAYER lookup in
// resolveProjection. The per-TEAM path (resolveTeamRows) deliberately does NOT go
// through this: it needs a live read, and it needs to know a team is the user's
// even when that team's rows would collide with a house player's key.
let _userIndex = null;
function userIndex() {
  if (_userIndex) return _userIndex;
  const idx = new Map();
  for (const abbrev of listUserTeams()) {
    for (const r of readUserTeamRows(abbrev)) idx.set(projKey(r.player, r.pos), r);
  }
  _userIndex = idx;
  return idx;
}

export function invalidateProjectionCache() {
  _userIndex = null;
  _curves = null;
  _rookiePrior = null;
}

/**
 * Discard one team's saved projection and fall back to the house set. THE ONLY
 * SANCTIONED DELETE PATH — callers must not reach for `removeProjlabTeam`
 * directly, because the cache invalidation below is not optional: `_userIndex`
 * is memoised over every projlab key, so a removal without it leaves the board,
 * the Rankings tab and My Value serving rows out of a storage key that no longer
 * exists, until something unrelated happens to invalidate.
 *
 * Returns true when a save was actually removed (false when the team was already
 * on the house set, or storage refused).
 */
export function clearUserTeam(abbrev) {
  const removed = removeProjlabTeam(abbrev);
  invalidateProjectionCache();
  return removed;
}

/**
 * SYNC EVERY TEAM TO THE HOUSE SET — the whole Lab back to the numbers Draft
 * Plan, the auction board and auto Mock1 Value read.
 *
 * 🔴 "UPDATE" IS "CLEAR", and that is a property of the resolution rule, not a
 * shortcut: `resolveTeamRows` gives a team to the user's save WHOLESALE or to
 * the house WHOLESALE — there is no per-row diff to merge a refresh into. So
 * syncing to house means removing the user layers, and there are THREE of them.
 * Removing only the saves would leave the other two to re-apply stale numbers
 * the moment a team is reopened:
 *   1. projlab-<TEAM> saves      — the rows themselves
 *   2. per-player rate edits     — EfficiencyEditor layers these back on rebuild
 *   3. per-team depth overrides  — old starter swaps fight the refreshed depth
 *
 * The CALLER owns the safety: take a backup (buildProjlabExport covers all three)
 * and confirm before calling. This util just does it.
 *
 * Returns { teams, playerEdits } — what was actually removed, for the toast.
 */
export function syncAllToHouse() {
  // Snapshot the list first: listUserTeams() re-reads localStorage, and removing
  // keys while enumerating them is how you skip every other entry.
  const teams = listUserTeams();
  for (const abbrev of teams) removeProjlabTeam(abbrev);
  const playerEdits = clearAllPlayerEdits();
  clearAllDepthOverrides();
  // ONE invalidation at the end rather than one per team — the cache is global
  // and nothing reads it in between.
  invalidateProjectionCache();
  return { teams: teams.length, playerEdits };
}

// ── Tier (c): the market curve ─────────────────────────────────────────────
//
// Derived AT RUNTIME from tier (b), not hardcoded, for one reason that matters:
// the fallback has to be CONTINUOUS with the tier above it. A player at ADP
// posRank 55 who missed the depth chart should be worth about what the charted
// player at posRank 54 is worth. Hardcoded constants would drift out of step with
// every `npm run gen-projections` refresh; deriving them means both move together.
//
// SHAPE: isotonic (monotone non-increasing) regression on the empirical
// (adpPosRank -> FULL_PPR points) ladder, then linear interpolation between
// knots. NOT a power law: a log-log OLS was tried first and over-predicts the
// head grossly (RB rank 1 -> 701 pts against an actual 345, R2 0.30 at QB)
// because the fit is pulled by a noisy tail. Isotonic tracks the real ladder at
// every rank, is monotone by construction, and needs no functional form.

// Pool-adjacent-violators, non-increasing. Returns fitted values aligned to ys.
function isotonicDecreasing(ys) {
  const blocks = ys.map((y) => ({ sum: y, n: 1 }));
  const out = [];
  for (const b of blocks) {
    out.push(b);
    // While the last block is HIGHER than the one before it, the sequence is
    // increasing — merge them into their common mean.
    while (out.length > 1) {
      const cur = out[out.length - 1], prev = out[out.length - 2];
      if (prev.sum / prev.n >= cur.sum / cur.n) break;
      out.pop(); out.pop();
      out.push({ sum: prev.sum + cur.sum, n: prev.n + cur.n });
    }
  }
  const fitted = [];
  for (const b of out) { const m = b.sum / b.n; for (let i = 0; i < b.n; i++) fitted.push(m); }
  return fitted;
}

const posRankOf = (name, pos, fallbackPosRank) => {
  let fb = fallbackPosRank;
  if (fb == null) {
    const hit = UNIQUE_PLAYERS.find((p) => p.pos === pos && stripSuffix(p.name) === stripSuffix(name));
    fb = hit?.posRank;
  }
  return parseRankNum(getEffectivePosRank(name, fb));
};

let _curves = null;
function curves() {
  if (_curves) return _curves;
  const byPos = {};
  for (const pos of CURVE_POSITIONS) byPos[pos] = [];
  for (const [team, rows] of Object.entries(DEFAULT_PROJECTIONS)) {
    for (const r of rows) {
      if (!CURVE_POSITIONS.includes(r.pos)) continue;
      const pts = scoreFromStats(r.stats, r.pos, FULL_PPR_LEGACY, r.games);
      const rank = posRankOf(r.player, r.pos, null);
      if (!(pts > 0) || !(rank > 0) || rank >= 900) continue;
      byPos[r.pos].push({ rank, pts, stats: r.stats, games: r.games, player: r.player, team });
    }
  }
  const out = {};
  for (const pos of CURVE_POSITIONS) {
    const pts = byPos[pos].slice().sort((a, b) => a.rank - b.rank);
    const fitted = isotonicDecreasing(pts.map((p) => p.pts));
    out[pos] = {
      knots: pts.map((p, i) => ({ rank: p.rank, pts: fitted[i] })),
      donors: pts,
      maxPts: pts.length ? Math.max(...pts.map((p) => p.pts)) : 0,
    };
  }
  _curves = out;
  return out;
}

/** Expected FULL_PPR points at an ADP posRank, from the isotonic ladder.
 *  Beyond the deepest knot the last knot's level decays gently rather than
 *  falling off a cliff — a rank-300 WR is a $1 player, not a zero. */
export function marketCurvePoints(pos, rank) {
  const c = curves()[pos];
  if (!c || !c.knots.length) return 0;
  const k = c.knots;
  const r = Math.max(1, rank);
  if (r <= k[0].rank) return k[0].pts;
  const last = k[k.length - 1];
  if (r >= last.rank) {
    // 1.5% per rank past the end, floored at 25% of the deepest charted level.
    const decay = Math.max(0.25, 1 - 0.015 * (r - last.rank));
    return last.pts * decay;
  }
  for (let i = 1; i < k.length; i++) {
    if (r <= k[i].rank) {
      const a = k[i - 1], b = k[i];
      if (b.rank === a.rank) return b.pts;
      const t = (r - a.rank) / (b.rank - a.rank);
      return a.pts + t * (b.pts - a.pts);
    }
  }
  return last.pts;
}

// The log(pick) prior, also fit at runtime off tier (b). Fit in
// fraction-of-position-max space so the four positions can be pooled — there are
// only ~28 charted players carrying draft capital, which is far too thin to fit
// per position. R2 ~0.79 on that pooled fit at the aug17 backup.
// 🔴 PROVISIONAL: 28 points is a prior, not a calibration.
const PRIOR_FALLBACK = { c: 0.7713, d: -0.1275 };
const PRIOR_MIN_N = 10;

let _rookiePrior = null;
function rookiePrior() {
  if (_rookiePrior) return _rookiePrior;
  const xs = [], ys = [];
  const cs = curves();
  for (const [, rows] of Object.entries(DEFAULT_PROJECTIONS)) {
    for (const r of rows) {
      const cap = DRAFT_CAPITAL_2026[stripSuffix(r.player)];
      const max = cs[r.pos]?.maxPts;
      if (!cap?.pick || !(max > 0)) continue;
      const pts = scoreFromStats(r.stats, r.pos, FULL_PPR_LEGACY, r.games);
      if (!(pts > 0)) continue;
      xs.push(Math.log(cap.pick));
      ys.push(pts / max);
    }
  }
  if (xs.length < PRIOR_MIN_N) { _rookiePrior = { ...PRIOR_FALLBACK, n: xs.length, fitted: false }; return _rookiePrior; }
  const n = xs.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n, my = ys.reduce((a, b) => a + b, 0) / n;
  let sxy = 0, sxx = 0;
  for (let i = 0; i < n; i++) { sxy += (xs[i] - mx) * (ys[i] - my); sxx += (xs[i] - mx) ** 2; }
  const d = sxx > 0 ? sxy / sxx : 0;
  _rookiePrior = { c: my - d * mx, d, n, fitted: true };
  return _rookiePrior;
}

/** Draft-capital-implied FULL_PPR points, or null with no capital. */
export function draftCapitalPoints(name, pos) {
  const cap = DRAFT_CAPITAL_2026[stripSuffix(name)];
  if (!cap?.pick) return null;
  const max = curves()[pos]?.maxPts;
  if (!(max > 0)) return null;
  const { c, d } = rookiePrior();
  const frac = c + d * Math.log(Math.max(1, cap.pick));
  return Math.max(0, frac) * max;
}

// Scale a donor stat line so it scores `targetPts` under FULL_PPR_LEGACY.
// Exact because FULL_PPR_LEGACY declares no yardage-bonus thresholds, so
// scoreFromStats is strictly linear in the stats under that lens — the league's
// own (non-linear) bonuses then apply to the scaled line at read time.
function scaleStats(stats, pos, games, targetPts) {
  const base = scoreFromStats(stats, pos, FULL_PPR_LEGACY, games);
  if (!(base > 0)) return { ...stats };
  const f = targetPts / base;
  const out = {};
  for (const [k, v] of Object.entries(stats)) {
    out[k] = typeof v === "number" ? Math.round(v * f * 10) / 10 : v;
  }
  return out;
}

const DONOR_K = 5;

/** Tier (c). Synthesises a stat line, never a bare point total, so the league
 *  lens still governs. Returns null only when the position has no curve. */
export function marketFallback(name, pos, fallbackPosRank) {
  const c = curves()[pos];
  if (!c || !c.donors.length) return null;
  const rank = posRankOf(name, pos, fallbackPosRank);
  const curvePts = marketCurvePoints(pos, rank);

  // Draft capital may only LIFT a player the market has not priced, and only
  // halfway — it is a prior over a thin fit, not a measurement. A rookie the
  // market already ranks highly keeps the market's number.
  const capPts = draftCapitalPoints(name, pos);
  const targetPts = capPts != null && capPts > curvePts
    ? curvePts + 0.5 * (capPts - curvePts)
    : curvePts;

  // Shape comes from the k charted players nearest in posRank, averaged
  // component-wise, then scaled to the target level. Nearest-in-rank rather than
  // a positional mean so a deep-tail WR inherits a deep-tail WR's usage mix.
  const near = c.donors
    .slice()
    .sort((a, b) => Math.abs(a.rank - rank) - Math.abs(b.rank - rank))
    .slice(0, Math.min(DONOR_K, c.donors.length));
  const shape = {};
  for (const d of near) {
    for (const [k, v] of Object.entries(d.stats)) {
      if (typeof v === "number") shape[k] = (shape[k] || 0) + v / near.length;
    }
  }
  const gamesList = near.map((d) => d.games).sort((a, b) => a - b);
  const games = gamesList[Math.floor(gamesList.length / 2)] ?? 17;

  return {
    stats: scaleStats(shape, pos, games, Math.max(0, targetPts)),
    games,
    source: SOURCE_FALLBACK,
    rank,
    curvePts,
    capitalPts: capPts,
    targetPts,
  };
}

// ── The public resolver ────────────────────────────────────────────────────

/** player: { name, pos, posRank? }. Returns { stats, games, source, ... }.
 *  Never returns null for a QB/RB/WR/TE; DEF/K resolve to SOURCE_LEGACY. */
export function resolveProjection(player) {
  const name = player?.name, pos = player?.pos;
  if (!name || !pos) return null;

  if (!CURVE_POSITIONS.includes(pos)) {
    // DEF/K — see the header carve-out. Stats stay in engine vocabulary here
    // because calculateProjection is what scores them.
    const rank = posRankOf(name, pos, player.posRank);
    return {
      stats: generatePlayerStats(pos, rank, name),
      games: 17,
      source: SOURCE_LEGACY,
      rank,
    };
  }

  const k = projKey(name, pos);
  const user = userIndex().get(k);
  if (user) return { ...user, source: SOURCE_USER };
  const house = defaultIndex().get(k);
  if (house) return { ...house, source: SOURCE_DEFAULT };
  return marketFallback(name, pos, player.posRank);
}

/** Season points under a league lens. THE number every consumer shows.
 *  Non-finite guard: `calculateProjection` multiplies by `scoring.<field>`
 *  directly, so a partial lens (one without the DEF/K weights, e.g. the bare
 *  FULL_PPR_LEGACY constant) yields NaN rather than 0. A NaN reaching a sort
 *  comparator silently scrambles the order, so it is clamped here at the exit. */
export function projectPoints(player, scoring = FULL_PPR_LEGACY) {
  const r = resolveProjection(player);
  if (!r) return 0;
  const pts = r.source === SOURCE_LEGACY
    ? calculateProjection(r.stats, player.pos, scoring)
    : Math.round(scoreFromStats(r.stats, player.pos, scoring, r.games));
  return Number.isFinite(pts) ? pts : 0;
}

/**
 * THE GAMES `projectPoints` SCORED — i.e. the ONLY correct divisor for a
 * per-game rate.
 *
 * 🔴 IT IS NOT 17, AND IT IS NOT A CONSTANT. Tiers (a)-(c) are normalised to a
 * partial season on purpose — the house set is mostly 15 games, a user's ProjLab
 * save carries whatever its stints say, and the market curve inherits its donors'
 * median — because average games missed is priced INTO the season total. DEF and
 * K are the exception: `generatePlayerStats` extrapolates a short season up to 17
 * (`v * 17 / g`) before `calculateProjection` sees it, so their line really is a
 * 17-game line and 17 really is their divisor. That is why a K once out-projected
 * a TE per game — same numerator scale, wrong denominator on one of them.
 *
 * Guarded rather than trusted: a 0 or a missing `games` would divide a rate to
 * Infinity, which renders as "Infinity" on a card rather than as an obvious bug.
 */
export function projectGames(player) {
  const r = resolveProjection(player);
  const g = Number(r?.games);
  return Number.isFinite(g) && g > 0 ? g : 17;
}

/**
 * The same divisor for a PROJLAB-SHAPED ROW — `{ player, pos, games, stats }`, as
 * `loadAllProjectedPlayers` and a save's `effSummary.players` produce them. The
 * row's own `games` is authoritative: it is the number its `pts` were scored
 * over, so it is the only divisor that reproduces the rate the rest of the app
 * shows for that player.
 *
 * The fallback is `projectGames`, NOT a literal 17. A save written before
 * `buildPlayerResult` carried `games` has none on the row, and 17 is exactly the
 * wrong guess for a skill player — it is what this whole fix removed.
 */
export function projectGamesForRow(row) {
  const g = Number(row?.games);
  if (Number.isFinite(g) && g > 0) return g;
  return projectGames({ name: row?.player ?? row?.name, pos: row?.pos });
}

/** Resolved stats in ENGINE vocabulary, for the PlayerList stat rows only.
 *  Display-side translation — points are never computed from this. */
export function displayStats(player) {
  const r = resolveProjection(player);
  if (!r) return {};
  return r.source === SOURCE_LEGACY ? r.stats : toEngineVocab(r.stats);
}

/**
 * The whole projected field, for replacement-level / VORP / My Value work.
 * Shaped like `projectAllDefaults` output ({ player, pos, team, stats, games })
 * so `buildDraftValues` consumes it unchanged — which is what makes the results
 * page and the My Value column agree by construction rather than by luck.
 *
 * `excludeTeams` drops whole NFL teams (ProjectionLab passes the teams it HAS
 * saved, so its own rows stay authoritative and are not double-counted).
 *
 * `includeFallback` (default true) appends a tier-(c) row for every skill player
 * in UNIQUE_PLAYERS that neither index covers. It defaults ON so this really is
 * "the whole field": with it off, an uncovered player has a points number on the
 * results page but no My Value row, and the two surfaces would disagree by
 * omission — which is exactly the parity this layer exists to guarantee.
 */
export function projectionField({ excludeTeams = null, includeFallback = true } = {}) {
  const out = [];
  const seen = new Set();
  const push = (name, pos, team, stats, games, source) => {
    const k = projKey(name, pos);
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ player: name, pos, team, stats, games, source });
  };
  // Per TEAM, not per player — see resolveTeamRows. A team the user has saved
  // contributes ONLY the user's rows; its house rows are skipped entirely, so an
  // edit that removes a player really removes them from the field.
  for (const abbrev of allTeamAbbrevs()) {
    if (excludeTeams && excludeTeams.has(abbrev)) continue;
    const { rows, source } = resolveTeamRows(abbrev);
    for (const r of rows) push(r.player, r.pos, abbrev, r.stats, r.games, source);
  }
  if (includeFallback) {
    for (const p of UNIQUE_PLAYERS) {
      if (!CURVE_POSITIONS.includes(p.pos)) continue;
      if (seen.has(projKey(p.name, p.pos))) continue;
      const fb = marketFallback(p.name, p.pos, p.posRank);
      if (fb) push(p.name, p.pos, p.team, fb.stats, fb.games, SOURCE_FALLBACK);
    }
  }
  return out;
}

/** Diagnostics for tests and the Lab's provenance label. */
export function projectionSourceStats() {
  const cs = curves();
  return {
    userCount: userIndex().size,
    defaultCount: defaultIndex().size,
    prior: rookiePrior(),
    curves: Object.fromEntries(CURVE_POSITIONS.map((p) => [p, {
      knots: cs[p].knots.length,
      maxPts: Math.round(cs[p].maxPts),
      deepestRank: cs[p].knots.length ? cs[p].knots[cs[p].knots.length - 1].rank : null,
    }])),
  };
}
